import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { Button } from '@/shared/components/ui/button';
import { useSubscriptions } from '../hooks/useSubscriptions';
import { SubscriptionStatusBadge } from '../components/SubscriptionStatusBadge';
import type { SubscriptionStatus } from '../types';

const ALL_STATUSES: SubscriptionStatus[] = [
  'trialing',
  'active',
  'past_due',
  'cancelled',
  'expired',
  'suspended',
];

export function SubscriptionsListPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | ''>('');

  const { data, isLoading, error } = useSubscriptions({
    ...(statusFilter !== '' && { status: statusFilter }),
  });

  const canCreate = user?.permissions.includes('subscriptions.create');
  const subscriptions = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-destructive">{t('common.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-foreground">{t('subscriptions.title')}</h1>
        {canCreate && (
          <Button onClick={() => void navigate({ to: '/dashboard/subscriptions/new' as never })}>
            {t('subscriptions.create')}
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as SubscriptionStatus | '')}
          className="h-10 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="">{t('common.allStatuses')}</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`subscriptions.status.${s}`)}
            </option>
          ))}
        </select>
      </div>

      {subscriptions.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <p>{t('subscriptions.empty')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">
                  {t('common.id')}
                </th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">
                  {t('subscriptions.tenant')}
                </th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">
                  {t('common.status')}
                </th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">
                  {t('common.period')}
                </th>
                <th className="px-4 py-3 text-end font-medium text-muted-foreground" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {subscriptions.map((sub) => (
                <tr key={sub.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {sub.id.slice(0, 8)}…
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {sub.tenant_id.slice(0, 8)}…
                  </td>
                  <td className="px-4 py-3">
                    <SubscriptionStatusBadge status={sub.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(sub.current_period_start).toLocaleDateString()} –{' '}
                    {new Date(sub.current_period_end).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-end">
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() =>
                        void navigate({
                          to: '/dashboard/subscriptions/$subscriptionId',
                          params: { subscriptionId: sub.id },
                        })
                      }
                    >
                      {t('common.view')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.total !== undefined && (
        <p className="text-xs text-muted-foreground">
          {data.total} {t('subscriptions.title')}
        </p>
      )}
    </div>
  );
}
